import React from "react";
import ReactDOM from "react-dom";

import { SecondaryButton } from "./shared";
import { ChainProgress } from "./useCatalogApi";

/**
 * Friendly, human labels for the ingest chain's nodes. Falls back to "Step N"
 * for any chain whose node ids we don't recognize.
 */
const INGEST_STEP_LABELS: Record<string, string> = {
  "node-1": "Organizing your list",
  "node-2": "Checking the catalog for matches",
  "node-3": "Researching products on the web",
  "node-4": "Formatting the data",
  "node-5": "Converting & running safety checks",
  "node-6": "Preparing your review",
};

const STEP_HINTS: Record<string, string> = {
  "node-3": "This is the slow one — web lookups per product.",
};

const StepIcon: React.FC<{ status: string }> = ({ status }) => {
  if (status === "completed")
    return (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 text-sm text-green-700">
        ✓
      </span>
    );
  if (status === "failed")
    return (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-sm text-red-700">
        ✕
      </span>
    );
  if (status === "running")
    return (
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100">
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-indigo-300 border-t-indigo-600" />
      </span>
    );
  return (
    <span className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-xs text-gray-400">
      ○
    </span>
  );
};

export interface ChainRunOverlayProps {
  open: boolean;
  progress: ChainProgress | null;
  credits?: number | null;
  onCancel: () => void;
  /** When provided, shows a "Hide" control that runs the job in the background. */
  onHide?: () => void;
  /** Optional heading override (e.g. the batch label). */
  title?: string;
  /** Optional label map override; defaults to the ingest chain labels. */
  labels?: Record<string, string>;
}

/**
 * "We're working" dialog that lists each chain step with a live status (done /
 * running + progress / pending / failed). The run keeps going even if this
 * dialog is hidden (it runs in the background and stays in the jobs tray); Hide
 * dismisses the dialog, Cancel stops tracking the run.
 */
const ChainRunOverlay: React.FC<ChainRunOverlayProps> = ({
  open,
  progress,
  credits,
  onCancel,
  onHide,
  title,
  labels,
}) => {
  if (!open) return null;
  const nodes = progress?.nodes || [];
  const currentId = progress?.currentNodeId;
  const stepLabels = labels || INGEST_STEP_LABELS;
  const nameFor = (id: string): string => {
    if (stepLabels[id]) return stepLabels[id];
    const index = nodes.findIndex((n) => n.id === id);
    return `Step ${index >= 0 ? index + 1 : ""}`.trim();
  };
  const currentLabel = currentId ? nameFor(currentId) : "Starting…";

  const doneCount = nodes.filter((n) => n.status === "completed").length;
  const total = nodes.length;

  // Portal to <body> so the dimmed backdrop covers the entire viewport instead
  // of being clipped by a transformed ancestor (which left an unshaded strip).
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Working"
    >
      <div className="absolute inset-0 bg-gray-900/60" />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white shadow-2xl">
        <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-4">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-semibold text-gray-900">
              {title || "Researching your products…"}
            </div>
            <div className="text-xs text-gray-500">
              {currentLabel}
              {total > 0
                ? ` · step ${Math.min(doneCount + 1, total)} of ${total}`
                : ""}
            </div>
          </div>
          {onHide && (
            <button
              type="button"
              onClick={onHide}
              className="flex-shrink-0 rounded-md px-2 py-1 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              title="Hide — keep running in the background"
              aria-label="Hide and keep running in the background"
            >
              ▾ Hide
            </button>
          )}
        </div>

        <div className="px-5 py-4">
          {nodes.length === 0 ? (
            <div className="text-sm text-gray-500">Getting things ready…</div>
          ) : (
            <ol className="space-y-2.5">
              {nodes.map((n) => {
                const active = n.status === "running";
                return (
                  <li key={n.id} className="flex items-start gap-3">
                    <StepIcon status={n.status} />
                    <div className="min-w-0 flex-1">
                      <div
                        className={`text-sm ${
                          active
                            ? "font-semibold text-gray-900"
                            : n.status === "completed"
                            ? "text-gray-600"
                            : n.status === "failed"
                            ? "font-medium text-red-700"
                            : "text-gray-400"
                        }`}
                      >
                        {nameFor(n.id)}
                        {active && n.progress ? (
                          <span className="ml-2 rounded bg-indigo-50 px-1.5 py-0.5 text-xs font-normal text-indigo-700">
                            {n.progress}
                          </span>
                        ) : null}
                      </div>
                      {active && STEP_HINTS[n.id] && (
                        <div className="text-xs text-gray-400">
                          {STEP_HINTS[n.id]}
                        </div>
                      )}
                      {n.status === "failed" && n.error && (
                        <div className="mt-0.5 truncate text-xs text-red-500">
                          {n.error}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-5 py-3">
          <span className="text-xs text-gray-400">
            {credits != null
              ? `Charged ${credits.toLocaleString()} tokens`
              : "Working… You're charged only when the run finishes."}
          </span>
          <div className="flex items-center gap-2">
            {onHide && (
              <SecondaryButton type="button" onClick={onHide}>
                Run in background
              </SecondaryButton>
            )}
            <SecondaryButton type="button" onClick={onCancel}>
              Cancel
            </SecondaryButton>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ChainRunOverlay;
